import { basicSetup } from 'codemirror';
import { html } from '@codemirror/lang-html';
import { EditorState, Transaction } from '@codemirror/state';
import { EditorView, keymap, placeholder } from '@codemirror/view';
import { indentWithTab } from '@codemirror/commands';
import { HighlightStyle, syntaxHighlighting } from '@codemirror/language';
import { tags } from '@lezer/highlight';
import { openSearchPanel } from '@codemirror/search';

const theme = EditorView.theme({
  '&': { height: '100%', color: '#d1dfd3', backgroundColor: '#19231e', fontSize: '12px' },
  '.cm-scroller': { overflow: 'auto', fontFamily: 'var(--hdp-mono)', lineHeight: '1.85' },
  '.cm-content': { padding: '16px 0', caretColor: '#fff' },
  '.cm-gutters': { backgroundColor: '#19231e', color: '#819789', border: 'none' },
  '.cm-activeLine, .cm-activeLineGutter': { backgroundColor: '#ffffff08' },
  '&.cm-focused .cm-selectionBackground, .cm-selectionBackground': { backgroundColor: '#42674e !important' },
  '.cm-panels, .cm-tooltip': { backgroundColor: '#26382c', color: '#e5efe8', borderColor: '#526956' },
  '.cm-searchMatch': { backgroundColor: '#837238' },
}, { dark: true });
const colors = HighlightStyle.define([
  { tag: tags.tagName, color: '#9cceb0' },
  { tag: tags.attributeName, color: '#e1bf89' },
  { tag: [tags.string, tags.attributeValue], color: '#b9cf91' },
  { tag: tags.keyword, color: '#c5a7dc' },
  { tag: [tags.number, tags.bool], color: '#e9ac88' },
  { tag: tags.comment, color: '#84968b', fontStyle: 'italic' },
]);

export class SourceEditor {
  readonly el = document.createElement('div');
  private view: EditorView;
  private silent = false;
  constructor(onChange: (value: string) => void) {
    this.el.className = 'hdp-source';
    this.view = new EditorView({
      parent: this.el,
      state: EditorState.create({ extensions: [
        basicSetup, html(), keymap.of([indentWithTab]), theme, syntaxHighlighting(colors),
        EditorView.lineWrapping, EditorState.tabSize.of(2),
        EditorView.contentAttributes.of({ 'aria-label': 'HTML source', spellcheck: 'false' }),
        placeholder('Paste HTML or open a file. CSS and JavaScript work inside your document.'),
        EditorView.updateListener.of(update => {
          if (update.docChanged && !this.silent) onChange(update.state.doc.toString());
        }),
      ] }),
    });
  }
  setValue(value: string, history = false): void {
    if (this.view.state.doc.toString() === value) return;
    this.silent = true;
    try {
      this.view.dispatch({ changes: { from: 0, to: this.view.state.doc.length, insert: value }, annotations: Transaction.addToHistory.of(history) });
    } finally { this.silent = false; }
  }
  attachRoot(): void { this.view.setRoot(this.el.getRootNode() as Document | ShadowRoot); this.view.requestMeasure(); }
  search(): void { openSearchPanel(this.view); }
  destroy(): void { this.view.destroy(); }
}
